export type DevicesListType = { [key: string]: number };
export type OnPromiseAttrType = { devices: DevicesListType, current: string, old: string };
export type CallbackType = (attr: OnPromiseAttrType, ...args: any) => void;

type DeviceManagerType = {
    oldDevice: string,
    currentDevice: string,
    devices: DevicesListType,
    callbacks: CallbackType[],
    windowCallbacks: { [key: string]: { fn: CallbackType, args: (string|CallbackType|Element)[] } },
    init(): void,
    checkDevice(): Promise<OnPromiseAttrType>,
    getCurrentDeviceSize(): number,
    getCurrentDevice(): string,
    addCallback(fn: CallbackType): void,
    addWindowCallback(...args: (string|CallbackType|Element)[]): void,
    removeWindowCallback(name: string): void,
    executeCallbacks(attr: OnPromiseAttrType): void,
    executeWindowCallbacks(): void,
    isBetween(deviceToCompare: string, deviceReferenceMin: string, deviceReferenceMax: string): boolean,
    isGreaterThanOrEqual(deviceToCompare: string, deviceReference: string): boolean,
    isLowerThan(deviceToCompare: string, deviceReference: string): boolean,
};

/**
 * Manage breakpoints events
 */
const DeviceManager: DeviceManagerType = {
    oldDevice: 'null',
    currentDevice: 'null',
    devices: {
        xl: 1220,
        lg_paysage: 1024,
        lg: 992,
        md: 768,
        sm: 576,
        xs: 0,
    },
    callbacks: [],
    windowCallbacks: {},

    /**
     * Initialize events
     * 
     * @return {void}
     */
    init() : void {
        document.addEventListener('DOMContentLoaded', () => {
            this.checkDevice().then((attr: OnPromiseAttrType) => {
                this.executeCallbacks(attr);
                this.executeWindowCallbacks();
            }).catch(() => null);
        });

        window.addEventListener('resize', () => {
            this.executeWindowCallbacks();

            this.checkDevice().then((attr: OnPromiseAttrType) => {
                this.executeCallbacks(attr);
            }).catch(() => null);
        });
    },

    /**
     * Check if device has changed
     * 
     * @return {Promise<OnPromiseAttrType>}
     */
    checkDevice() : Promise<OnPromiseAttrType> {
        const winW = window.innerWidth;

        return new Promise((resolve, reject) => {
            this.oldDevice = this.currentDevice;

            let breakpointSup = 99999;
            for (const device in this.devices) {
                // Skip loop if the property is from prototype
                if (!Object.prototype.hasOwnProperty.call(this.devices, device)) {
                    continue;
                }
            
                const breakpoint = this.devices[device];

                if (winW >= breakpoint && winW < breakpointSup && this.oldDevice !== device) {
                    this.currentDevice = device;

                    resolve({ devices: this.devices, current: device, old: this.oldDevice });
                }

                breakpointSup = this.devices[device];
            }

            reject();
        });
    },

    /**
     * Get the current device size
     * 
     * @return {number}
     */
    getCurrentDeviceSize() : number {
        return this.devices[this.getCurrentDevice()];
    },
    
    /**
     * Get the current device name
     * 
     * @return {string}
     */
    getCurrentDevice() : string {
        return this.currentDevice;
    },

    /**
     * Add a callback when device change
     * 
     * @param {CallbackType} fn
     * 
     * @return {void}
     */
    addCallback(fn: CallbackType) : void {
        this.callbacks.push(fn);

        if (document.body.classList.contains('wp-admin')) {
            this.checkDevice().then((attr: OnPromiseAttrType) => {
                fn(attr);
            }).catch(() => null);
        }
    },

    /**
     * Add a callback when window is resized
     * 
     * @param {(string|CallbackType|Element)[]} args
     * 
     * Usage:
     * addWindowCallback('callback-name', function, arg_1_for_function, arg_2_for_function, ...);
     * 
     * @return {void}
     */
    addWindowCallback(...args: (string|CallbackType|Element)[]) : void {
        let name: string|null = null;
        let fn: CallbackType|null = null;

        let newArgsBeginAt = 0;

        if (typeof args[0] === 'string') {
            name = args[0];

            newArgsBeginAt = 1;

            if (typeof args[1] === 'function') {
                fn = args[1];

                newArgsBeginAt = 2;
            }
        } else if (typeof args[0] === 'function') {
            fn = args[0];

            newArgsBeginAt = 1;

            let newName = 'callback-' + Object.keys(this.windowCallbacks).length;
            if (this.windowCallbacks[newName]) {
                newName += '1';
            }

            name = newName;
        }

        const newArgs = [];
        for (let i = newArgsBeginAt; i < args.length; i++) {
            newArgs.push(args[i]);
        }

        if (typeof fn === 'undefined') {
            console.error('Function not defined');
            throw 'Function not defined';
        }

        if (fn && name) {
            this.windowCallbacks[name] = {
                fn,
                args: newArgs,
            };
        }

        if (document.body.classList.contains('wp-admin')) {
            this.checkDevice().then((attr: OnPromiseAttrType) => {
                if (fn) {
                    fn(attr);
                }
            }).catch(() => null);
        }
    },

    /**
     * Remove a callback from window callbacks array
     * 
     * @param {string} name
     * 
     * @return {void}
     */
    removeWindowCallback(name: string) : void {
        if (this.windowCallbacks[name]) {
            delete this.windowCallbacks[name];
        }
    },

    /**
     * Execute all callbacks
     * 
     * @param {OnPromiseAttrType} attr
     * 
     * @return {void}
     */
    executeCallbacks(attr: OnPromiseAttrType) : void {
        this.callbacks.forEach((callback: CallbackType) => {
            callback(attr);
        });
    },

    /**
     * Execute all window callbacks
     * 
     * @return {void}
     */
    executeWindowCallbacks() : void {
        Object.keys(this.windowCallbacks).forEach(key => {
            const callback = this.windowCallbacks[key].fn;
            const args = this.windowCallbacks[key].args;

            callback({ devices: this.devices, current: this.currentDevice, old: this.oldDevice }, ...(args as []));
        });
    },

    /**
     * Check if deviceToCompare is between deviceReferenceMin and deviceReferenceMax
     * 
     * @param {string} deviceToCompare
     * @param {string} deviceReferenceMin
     * @param {string} deviceReferenceMax
     * 
     * @return {boolean}
     */
    isBetween(deviceToCompare: string, deviceReferenceMin: string, deviceReferenceMax: string) : boolean {
        return this.isGreaterThanOrEqual(deviceToCompare, deviceReferenceMin) && this.isLowerThan(deviceToCompare, deviceReferenceMax);
    },

    /**
     * Check if deviceToCompare is greater than or equal deviceReference
     * 
     * @param {string} deviceToCompare
     * @param {string} deviceReference
     * 
     * @return {boolean}
     */
    isGreaterThanOrEqual(deviceToCompare: string, deviceReference: string) : boolean {
        return this.devices[deviceToCompare] >= this.devices[deviceReference];
    },

    /**
     * Check if deviceToCompare is lower than deviceReference
     * 
     * @param {string} deviceToCompare
     * @param {string} deviceReference
     * 
     * @return {boolean}
     */
    isLowerThan(deviceToCompare: string, deviceReference: string) : boolean {
        return this.devices[deviceToCompare] < this.devices[deviceReference];
    },
};

export default DeviceManager;
